import { alpha, Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import React, { useState } from 'react';
import { AlertRuleScreenProps } from 'components/alert-rule-popup/index';
import { alertRuleAPI } from 'api/alert-wizard';
import { Popup } from 'components/common/popup/Popup';
import AlertRuleChart from 'components/alert-rule-popup/AlertRuleChart';
import useBundleTranslation from 'i18n';

interface AlertRuleScreenTypeProps extends AlertRuleScreenProps {
    onChange?: (text: any) => void;
}

export default function AlertRuleScreenType(props: AlertRuleScreenTypeProps) {
    const { alertRuleData, wizardData, updateAlertRuleData = () => {} } = props;
    const { t } = useBundleTranslation(['components/alert-rule-popup/alert_rule_popup']);

    const [showPublicToPrivateConfirmation, setShowPublicToPrivateConfirmation] = useState<boolean>(false);
    const [publicToPrivateUsers, setPublicToPrivateUsers] = useState<string[]>([]);

    let alertTypes = ['fixed value', 'prior value', 'average', 'volatility band'];

    if (wizardData.isKpi) {
        const targetCount = wizardData.form.target_id?.data?.length > 0;
        if (targetCount) alertTypes.push('target');
        alertTypes.push('compound');
    } else {
        alertTypes = alertTypes.concat(['stoplight', 'unusual value', 'best ever', 'worst ever']);
    }

    const itemsData = alertTypes.map((alertTypeValue) => {
        const alertTypeKey = alertTypeValue.replaceAll(' ', '_');
        return {
            value: alertTypeValue,
            label: t(`alert_type.${alertTypeKey}.label`),
            description: (
                <Box
                    sx={{
                        '.mark': {
                            color: 'error.main',
                        },
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t(`alert_type.${alertTypeKey}.desc`, {
                            markTagSTART: '<span class="mark">',
                            markTagEND: '</span>',
                            newLineTag: '<br />',
                        }),
                    }}
                />
            ),
        };
    });

    return (
        <Box>
            <Box>
                <RadioGroup
                    value={alertRuleData.alert_type}
                    name="alert_rule"
                    onChange={(event) => {
                        if (alertRuleData.alert_type !== event.target.value)
                            updateAlertRuleData({ alert_type: event.target.value });
                    }}
                    sx={{ mb: 0 }}
                >
                    {itemsData.map((avOption) => {
                        const isCurrent = alertRuleData.alert_type == avOption.value;
                        return (
                            <Box
                                sx={{
                                    border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.24)}`,
                                    borderRadius: '4px',
                                    mt: 1.5,
                                    p: 1.5,
                                    '&:first-of-type': {
                                        mt: 0,
                                    },
                                    '&.active': {
                                        borderColor: (theme) => theme.palette.primary.main,
                                        boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
                                    },
                                    '&:not(.active):hover': {
                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                                        cursor: 'pointer',
                                    },
                                }}
                                className={isCurrent ? 'active' : undefined}
                                onClick={() => {
                                    updateAlertRuleData({ alert_type: avOption.value });
                                }}
                                key={avOption.value}
                            >
                                <FormControlLabel
                                    value={avOption.value}
                                    control={<Radio data-test={`alert-rule-${avOption.value}_radio`} />}
                                    label={
                                        <Box
                                            sx={{ fontWeight: 700, color: isCurrent ? 'primary.main' : 'text.primary' }}
                                        >
                                            {avOption.label}
                                        </Box>
                                    }
                                    sx={{ my: '-5px', pointerEvents: 'none' }}
                                />
                                {isCurrent && (
                                    <Box
                                        sx={{
                                            pl: 3,
                                            pt: 0.5,
                                            color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                        }}
                                    >
                                        {avOption.description}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </RadioGroup>
            </Box>
            <Box>
                {wizardData.isKpi && (
                    <Box id={'alertrule_form_alert_visibility'}>
                        {wizardData.priv_share_rule && (
                            <>
                                <FormControl
                                    focused={false}
                                    sx={{
                                        width: 1,
                                        border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                        borderRadius: 1,
                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.02),
                                        p: 1.5,
                                        mt: 2,
                                    }}
                                >
                                    <FormLabel>{t('form.type.alert_visibility.title')}</FormLabel>
                                    <RadioGroup
                                        value={alertRuleData.alert_visibility}
                                        name="alert_visibility"
                                        onChange={(event) => {
                                            const newVal = event.target.value;

                                            if (newVal === 'private') {
                                                //remove workflow
                                                /*if (Number(alertRuleData.workflow_id) > 0) {
                                                    //ToDo [Stas] Change to our confirm ui popup
                                                    alert(t('form.type.alert_visibility.private_workflow_warning'));
                                                    return false;
                                                }*/
                                                if (Number(alertRuleData.alert_rule_id) > 0) {
                                                    alertRuleAPI
                                                        .getSubscribedUsers(Number(alertRuleData.alert_rule_id))
                                                        .then((response) => {
                                                            let users = (response?.data ?? []).map((user: any) => {
                                                                return user.display_name;
                                                            });
                                                            setPublicToPrivateUsers(users);
                                                            if (users.length) {
                                                                setShowPublicToPrivateConfirmation(true);
                                                            } else {
                                                                updateAlertRuleData({ alert_visibility: 'private' });
                                                            }
                                                        })
                                                        .catch(() => {});
                                                } else {
                                                    updateAlertRuleData({ alert_visibility: event.target.value });
                                                }
                                            } else {
                                                updateAlertRuleData({ alert_visibility: event.target.value });
                                            }
                                        }}
                                    >
                                        {['public', 'private'].map((avOption) => {
                                            return (
                                                <FormControlLabel
                                                    key={avOption}
                                                    value={avOption}
                                                    control={
                                                        <Radio data-test={`alert-rule-option-${avOption}_radio`} />
                                                    }
                                                    label={
                                                        <>
                                                            <Box component="span">
                                                                {t(`form.type.alert_visibility.option_${avOption}`)}
                                                            </Box>{' '}
                                                            ({t(`form.type.alert_visibility.option_${avOption}_desc`)})
                                                        </>
                                                    }
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                                <Popup
                                    settings={{
                                        title: t(
                                            `form.type.alert_visibility.users_subscribed_to_this_alert_rule_title`,
                                        ),
                                        textOK: t(`form.type.alert_visibility.users_subscribed_to_this_alert_rule_ok`),
                                    }}
                                    open={showPublicToPrivateConfirmation}
                                    onHide={() => {
                                        setShowPublicToPrivateConfirmation(false);
                                    }}
                                    onConfirm={() => {
                                        updateAlertRuleData({ alert_visibility: 'private' });
                                        setShowPublicToPrivateConfirmation(false);
                                    }}
                                    maxWidth="popupMd"
                                >
                                    <Box>{t('form.type.alert_visibility.users_subscribed_to_this_alert_rule')}</Box>
                                    {publicToPrivateUsers.length > 0 && (
                                        <Box sx={{ mt: 2, maxHeight: 500, overflow: 'auto' }}>
                                            {publicToPrivateUsers.map((userName) => {
                                                return <Box key={userName}>{userName}</Box>;
                                            })}
                                        </Box>
                                    )}
                                </Popup>
                            </>
                        )}
                        <Box
                            id={'alertElementPreview'}
                            sx={{
                                height: 182,
                                width: 1,
                            }}
                        >
                            <AlertRuleChart
                                elementId={wizardData.dashboardElement.element_id}
                                segmentValueId={wizardData.dashboardElement.segment_value_id}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
